import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "../../components/ui/command";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../../components/ui/dropdown-menu";
import { Plus, Pencil, Trash2, Shield, RefreshCw, ChevronsUpDown, Check, Download, FileSpreadsheet, FileText, File, ShieldCheck, ShieldAlert, Printer, Loader2, AlertTriangle } from "lucide-react";
import { useAuth } from "../../App";
import { toast } from "sonner";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";
import { jsPDF } from "jspdf";
import { applyPlugin } from "jspdf-autotable";

applyPlugin(jsPDF);

const SolvenciaPublicidad = () => {
  const { API, token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({});
  const [contribuyentes, setContribuyentes] = useState([]);
  const [contribuyentesSinLae, setContribuyentesSinLae] = useState([]);
  const [rifPopoverOpen, setRifPopoverOpen] = useState(false);
  const [filters, setFilters] = useState({ rif: "", nombre: "", fecha: "", nro_recibo: "", estatus: "", nro_solvencia: "" });
  const [verificando, setVerificando] = useState({});
  const [detalleDialog, setDetalleDialog] = useState(null);
  const [validandoRecibo, setValidandoRecibo] = useState(false);
  const [reciboValidado, setReciboValidado] = useState(null);

  useEffect(() => { fetchData(); fetchContribuyentes(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const resp = await axios.get(`${API}/publicidad/solvencia-publicidad`, { headers });
      setData(resp.data);
    } catch { toast.error("Error al cargar datos"); }
    setLoading(false);
  };

  const fetchContribuyentes = async () => {
    try {
      const [respLae, respSinLae] = await Promise.all([
        axios.get(`${API}/contribuyentes/con-lae`, { headers }),
        axios.get(`${API}/contribuyentes/sin-lae`, { headers })
      ]);
      setContribuyentes(respLae.data);
      setContribuyentesSinLae(respSinLae.data);
    } catch { /* ignore */ }
  };

  const handleValidarRecibo = async () => {
    if (!formData.nro_recibo) { toast.error("Ingrese un número de recibo"); return; }
    if (!formData.rif) { toast.error("Seleccione un contribuyente primero"); return; }
    setValidandoRecibo(true);
    try {
      const resp = await axios.get(`${API}/recaudaciones/validar-recibo-publicidad?nro_recibo=${formData.nro_recibo}&rif=${formData.rif}`, { headers });
      const d = resp.data;
      if (d.valido) {
        setReciboValidado(d.comprobante);
        toast.success(d.mensaje);
      } else {
        setReciboValidado(null);
        toast.error(d.mensaje);
      }
    } catch { toast.error("Error al validar recibo"); setReciboValidado(null); }
    setValidandoRecibo(false);
  };

  const handleSave = async () => {
    if (!formData.rif) { toast.error("Seleccione un contribuyente"); return; }
    try {
      if (editingItem) {
        await axios.put(`${API}/publicidad/solvencia-publicidad/${editingItem.id}`, formData, { headers });
        toast.success("Solvencia actualizada");
      } else {
        const resp = await axios.post(`${API}/publicidad/solvencia-publicidad`, formData, { headers });
        const d = resp.data;
        if (d.estatus === "Solvente") {
          toast.success("Contribuyente SOLVENTE - Todos los trimestres están pagados");
        } else {
          toast.warning(d.message, { duration: 8000 });
        }
      }
      setDialogOpen(false); setEditingItem(null); setFormData({}); fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al guardar"); }
  };

  const handleVerificar = async (item) => {
    setVerificando(prev => ({ ...prev, [item.id]: true }));
    try {
      const resp = await axios.get(`${API}/publicidad/solvencia-publicidad/${item.id}/verificar`, { headers });
      const d = resp.data;
      if (d.estatus === "Solvente") {
        toast.success(`${item.nombre_razon_social}: SOLVENTE - Todos los trimestres pagados`);
      } else {
        toast.warning(`${item.nombre_razon_social}: MOROSO - ${d.verificacion.total_pendientes} cálculos pendientes`, { duration: 6000 });
      }
      fetchData();
    } catch { toast.error("Error al verificar"); }
    setVerificando(prev => ({ ...prev, [item.id]: false }));
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Está seguro de eliminar este registro?")) return;
    try {
      await axios.delete(`${API}/publicidad/solvencia-publicidad/${id}`, { headers });
      toast.success("Registro eliminado"); fetchData();
    } catch { toast.error("Error al eliminar"); }
  };

  const openCreate = () => {
    setEditingItem(null);
    setFormData({ clase_contribuyente: "Contribuyente con LAE", fecha: new Date().toISOString().split("T")[0] });
    setReciboValidado(null);
    setDialogOpen(true);
  };

  const openEdit = (item) => {
    setEditingItem(item);
    setFormData({ ...item });
    setReciboValidado(null);
    setDialogOpen(true);
  };

  const handlePrint = (item) => {
    const doc = new jsPDF();
    doc.setFontSize(16);
    doc.text("Solvencia de Publicidad y Propaganda Comercial", 105, 20, { align: "center" });
    doc.setFontSize(10);
    doc.text(`Fecha: ${item.fecha}`, 20, 35);
    doc.text(`Clase Contribuyente: ${item.clase_contribuyente}`, 20, 42);
    doc.text(`RIF: ${item.rif}`, 20, 49);
    doc.text(`Nombre: ${item.nombre_razon_social}`, 20, 56);
    doc.text(`Nro. Recibo: ${item.nro_recibo || "-"}`, 20, 63);
    doc.setFontSize(14);
    doc.setTextColor(22, 163, 74);
    doc.text("ESTATUS: SOLVENTE", 105, 80, { align: "center" });
    doc.save(`solvencia_publicidad_${item.rif}_${item.fecha}.pdf`);
    toast.success("Solvencia impresa");
  };

  const filtered = data.filter(item => {
    if (filters.nro_solvencia && !(item.nro_solvencia_pub || "").toLowerCase().includes(filters.nro_solvencia.toLowerCase())) return false;
    if (filters.rif && !((item.rif || "") + " " + (item.nombre_razon_social || "")).toLowerCase().includes(filters.rif.toLowerCase())) return false;
    if (filters.fecha && !(item.fecha || "").includes(filters.fecha)) return false;
    if (filters.nro_recibo && !(item.nro_recibo || "").toLowerCase().includes(filters.nro_recibo.toLowerCase())) return false;
    if (filters.estatus && !(item.estatus || "").toLowerCase().includes(filters.estatus.toLowerCase())) return false;
    return true;
  });

  // Export functions
  const exportToExcel = () => {
    const rows = filtered.map(f => ({ "Solvencia Pub. Nro.": f.nro_solvencia_pub || "-", "Clase": f.clase_contribuyente, "RIF": f.rif, "Nombre": f.nombre_razon_social, "Fecha": f.fecha, "Nro. Recibo": f.nro_recibo, "Estatus": f.estatus }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Solvencia Publicidad");
    XLSX.writeFile(wb, `solvencia_publicidad_${new Date().toISOString().split("T")[0]}.xlsx`);
  };
  const exportToPDF = () => {
    const doc = new jsPDF({ orientation: "landscape" });
    doc.setFontSize(14); doc.text("Solvencia Publicidad y Propaganda Comercial", 14, 15);
    doc.setFontSize(9); doc.text(`Generado: ${new Date().toLocaleDateString("es-VE")} | Total: ${filtered.length}`, 14, 22);
    doc.autoTable({ startY: 28, head: [["Clase", "RIF", "Nombre", "Fecha", "Recibo", "Estatus"]], body: filtered.map(f => [f.clase_contribuyente, f.rif, f.nombre_razon_social, f.fecha, f.nro_recibo || "-", f.estatus]), styles: { fontSize: 8 }, headStyles: { fillColor: [234, 88, 12] } });
    doc.save(`solvencia_publicidad_${new Date().toISOString().split("T")[0]}.pdf`);
  };
  const exportToCSV = () => {
    const h = "Clase,RIF,Nombre,Fecha,Nro. Recibo,Estatus\n";
    const csv = h + filtered.map(f => `${f.clase_contribuyente},${f.rif},"${(f.nombre_razon_social || "").replace(/"/g, '""')}",${f.fecha},${f.nro_recibo || "-"},${f.estatus}`).join("\n");
    saveAs(new Blob([csv], { type: "text/csv;charset=utf-8" }), `solvencia_publicidad_${new Date().toISOString().split("T")[0]}.csv`);
  };

  const listaContribuyentes = formData.clase_contribuyente === "Contribuyente sin LAE" ? contribuyentesSinLae : contribuyentes;

  return (
    <div className="p-4 md:p-6 space-y-4" data-testid="solvencia-publicidad-page">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-orange-800 flex items-center gap-2">
            <Shield className="w-6 h-6" />
            Solvencia Publicidad y Propaganda Comercial
          </h2>
          <p className="text-sm text-slate-500">{filtered.length} registros</p>
        </div>
        <div className="flex gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="gap-2"><Download className="w-4 h-4" /> Exportar</Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={exportToExcel} className="gap-2 cursor-pointer"><FileSpreadsheet className="w-4 h-4" /> Excel</DropdownMenuItem>
              <DropdownMenuItem onClick={exportToPDF} className="gap-2 cursor-pointer"><FileText className="w-4 h-4" /> PDF</DropdownMenuItem>
              <DropdownMenuItem onClick={exportToCSV} className="gap-2 cursor-pointer"><File className="w-4 h-4" /> CSV</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button variant="outline" onClick={fetchData} disabled={loading} className="gap-2">
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Actualizar
          </Button>
          <Button onClick={openCreate} className="gap-2 bg-orange-600 hover:bg-orange-700" data-testid="btn-nueva-solvencia-pub">
            <Plus className="w-4 h-4" /> Nueva Solvencia
          </Button>
        </div>
      </div>

      {/* Tabla */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b bg-orange-50">
                  <th className="px-4 py-3 text-left text-sm font-medium text-orange-700">Solvencia Pub. Nro.</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-orange-700">Clase Contribuyente</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-orange-700">RIF / Contribuyente</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-orange-700">Fecha</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-orange-700">Nro. Recibo</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-orange-700">Estatus</th>
                  <th className="px-4 py-3 text-center text-sm font-medium text-orange-700">Acciones</th>
                </tr>
                <tr className="border-b bg-white">
                  <th className="px-2 py-1"><Input className="h-7 text-xs" placeholder="Filtrar..." value={filters.nro_solvencia} onChange={e => setFilters({...filters, nro_solvencia: e.target.value})} /></th>
                  <th className="px-2 py-1"></th>
                  <th className="px-2 py-1"><Input className="h-7 text-xs" placeholder="Filtrar..." value={filters.rif} onChange={e => setFilters({...filters, rif: e.target.value})} /></th>
                  <th className="px-2 py-1"><Input className="h-7 text-xs" placeholder="Filtrar..." value={filters.fecha} onChange={e => setFilters({...filters, fecha: e.target.value})} /></th>
                  <th className="px-2 py-1"><Input className="h-7 text-xs" placeholder="Filtrar..." value={filters.nro_recibo} onChange={e => setFilters({...filters, nro_recibo: e.target.value})} /></th>
                  <th className="px-2 py-1"><Input className="h-7 text-xs" placeholder="Filtrar..." value={filters.estatus} onChange={e => setFilters({...filters, estatus: e.target.value})} /></th>
                  <th className="px-2 py-1"></th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={7} className="py-12 text-center text-slate-400">Cargando...</td></tr>
                ) : filtered.length === 0 ? (
                  <tr><td colSpan={7} className="py-12 text-center text-slate-400">No hay registros</td></tr>
                ) : filtered.map(item => (
                  <tr key={item.id} className={`border-b hover:bg-orange-50/30 ${item.estatus === "Solvente" ? "bg-green-50/20" : ""}`}>
                    <td className="px-4 py-2.5 text-sm font-mono font-medium text-slate-900">{item.nro_solvencia_pub || "-"}</td>
                    <td className="px-4 py-2.5 text-sm text-slate-600">{item.clase_contribuyente || "-"}</td>
                    <td className="px-4 py-2.5 text-sm text-slate-700">{item.rif} {item.nombre_razon_social ? `- ${item.nombre_razon_social}` : ""}</td>
                    <td className="px-4 py-2.5 text-sm text-slate-600">{item.fecha}</td>
                    <td className="px-4 py-2.5 text-sm font-mono text-slate-600">{item.nro_recibo || "-"}</td>
                    <td className="px-4 py-2.5 text-sm">
                      {item.estatus === "Solvente" ? (
                        <Badge className="bg-green-100 text-green-700 border-green-200 gap-1">
                          <ShieldCheck className="w-3 h-3" /> Solvente
                        </Badge>
                      ) : (
                        <Badge className="bg-red-100 text-red-700 border-red-200 gap-1">
                          <ShieldAlert className="w-3 h-3" /> Moroso
                        </Badge>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-center">
                      <div className="flex items-center justify-center gap-1">
                        {item.estatus === "Moroso" && (
                          <Button variant="ghost" size="sm" onClick={() => handleVerificar(item)} disabled={verificando[item.id]}
                            title="Verificar Solvencia" data-testid={`btn-verificar-${item.id}`}>
                            {verificando[item.id] ? <Loader2 className="w-4 h-4 animate-spin text-amber-600" /> : <ShieldCheck className="w-4 h-4 text-amber-600" />}
                          </Button>
                        )}
                        {item.estatus === "Moroso" && item.verificacion && (
                          <Button variant="ghost" size="sm" onClick={() => setDetalleDialog(item)} title="Ver detalle pendientes">
                            <AlertTriangle className="w-4 h-4 text-red-500" />
                          </Button>
                        )}
                        {item.estatus === "Solvente" && (
                          <Button variant="ghost" size="sm" onClick={() => handlePrint(item)} title="Imprimir Solvencia">
                            <Printer className="w-4 h-4 text-green-600" />
                          </Button>
                        )}
                        <Button variant="ghost" size="sm" onClick={() => openEdit(item)} title="Editar">
                          <Pencil className="w-4 h-4 text-blue-600" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(item.id)} title="Eliminar">
                          <Trash2 className="w-4 h-4 text-red-600" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Dialog detalle pendientes */}
      <Dialog open={!!detalleDialog} onOpenChange={(open) => !open && setDetalleDialog(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="w-5 h-5" /> Trimestres Pendientes por Pago
            </DialogTitle>
          </DialogHeader>
          {detalleDialog && detalleDialog.verificacion && (
            <div className="space-y-3 py-2">
              <p className="text-sm text-slate-600"><strong>{detalleDialog.nombre_razon_social}</strong> ({detalleDialog.rif})</p>
              <div className="grid grid-cols-2 gap-2">
                <div className="bg-red-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-red-700">{detalleDialog.verificacion.pendientes_tasas || 0}</p>
                  <p className="text-xs text-red-500">Tasas pendientes</p>
                </div>
                <div className="bg-amber-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-amber-700">{detalleDialog.verificacion.pendientes_avisos || 0}</p>
                  <p className="text-xs text-amber-500">Avisos/Vallas pendientes</p>
                </div>
              </div>
              <div className="bg-slate-50 rounded-lg p-3">
                <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Detalle:</p>
                <ul className="space-y-1">
                  {(detalleDialog.verificacion.detalle || []).map((d, i) => (
                    <li key={i} className="text-xs text-slate-700 flex items-center gap-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-red-400 flex-shrink-0" /> {d}
                    </li>
                  ))}
                </ul>
              </div>
              <p className="text-sm font-semibold text-red-700 text-right">
                Total: Bs. {(detalleDialog.verificacion.monto_total || 0).toLocaleString("es-VE", { minimumFractionDigits: 2 })}
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog crear/editar */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingItem ? "Editar Solvencia" : "Nueva Solvencia Publicidad"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Clase de Contribuyente</Label>
              <Select value={formData.clase_contribuyente || ""} onValueChange={v => setFormData({...formData, clase_contribuyente: v, rif: "", contribuyente_id: "", nombre_razon_social: ""})}>
                <SelectTrigger data-testid="form-clase-solv-pub"><SelectValue placeholder="Seleccione clase" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Contribuyente con LAE">Contribuyente con LAE</SelectItem>
                  <SelectItem value="Contribuyente sin LAE">Contribuyente sin LAE</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>RIF / Contribuyente</Label>
              <Popover open={rifPopoverOpen} onOpenChange={setRifPopoverOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-between h-10 font-normal" disabled={!formData.clase_contribuyente} data-testid="form-rif-solv-pub">
                    {formData.rif ? <span className="truncate">{formData.rif} - {formData.nombre_razon_social}</span> : <span className="text-slate-400">{formData.clase_contribuyente ? "Buscar contribuyente..." : "Seleccione clase primero"}</span>}
                    <ChevronsUpDown className="ml-2 h-4 w-4 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[400px] p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Buscar por RIF o nombre..." />
                    <CommandList>
                      <CommandEmpty>No encontrado</CommandEmpty>
                      <CommandGroup>
                        {listaContribuyentes.map(c => {
                          const rifFull = `${c.tipo_rif || "J"}-${c.rif}`;
                          return (
                            <CommandItem key={c.id} value={`${rifFull} ${c.nombre || c.denominacion_comercial || ""}`}
                              onSelect={() => { setFormData({...formData, rif: rifFull, contribuyente_id: c.id, nombre_razon_social: c.nombre || c.denominacion_comercial}); setRifPopoverOpen(false); }}>
                              <Check className={`mr-2 h-4 w-4 ${formData.rif === rifFull ? "opacity-100" : "opacity-0"}`} />
                              <div className="flex flex-col">
                                <span className="font-medium text-sm">{rifFull}</span>
                                <span className="text-xs text-slate-500">{c.nombre || c.denominacion_comercial}</span>
                              </div>
                            </CommandItem>
                          );
                        })}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>

            <div className="space-y-2">
              <Label>Fecha</Label>
              <Input type="date" value={formData.fecha || ""} onChange={e => setFormData({...formData, fecha: e.target.value})} data-testid="form-fecha-solv-pub" />
            </div>

            <div className="space-y-2">
              <Label>Nro. Recibo</Label>
              <div className="flex gap-2">
                <Input value={formData.nro_recibo || ""} onChange={e => { setFormData({...formData, nro_recibo: e.target.value}); setReciboValidado(null); }} placeholder="Número de recibo" data-testid="form-recibo-solv-pub" />
                <Button type="button" variant="outline" onClick={handleValidarRecibo} disabled={validandoRecibo || !formData.nro_recibo || !formData.rif} className="gap-1 whitespace-nowrap" data-testid="btn-validar-recibo-pub">
                  {validandoRecibo ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
                  Validar
                </Button>
              </div>
              {reciboValidado && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-2 text-xs text-green-700">
                  Recibo válido: {reciboValidado.nro_recibo} | Monto: Bs. {(reciboValidado.monto || 0).toLocaleString("es-VE", { minimumFractionDigits: 2 })} | Fecha: {reciboValidado.fecha_pago}
                </div>
              )}
            </div>

            {/* Estatus - solo lectura */}
            <div className="bg-slate-50 border rounded-lg p-3">
              <p className="text-xs text-slate-500 uppercase font-semibold mb-1">Estatus</p>
              <p className="text-sm text-slate-700">
                {editingItem ? (
                  <Badge className={editingItem.estatus === "Solvente" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}>
                    {editingItem.estatus}
                  </Badge>
                ) : (
                  <span className="text-amber-700">Se determinará automáticamente al guardar según los cálculos pendientes del contribuyente.</span>
                )}
              </p>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
              <Button onClick={handleSave} className="bg-orange-600 hover:bg-orange-700" data-testid="btn-guardar-solv-pub">
                {editingItem ? "Actualizar" : "Guardar"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SolvenciaPublicidad;
